import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { FileText, Download, Eye, Trash2, Plus, Leaf, Coins, Activity, Shield, TrendingUp, CheckCircle2, X, ChevronRight, ChevronLeft, Loader2, FileSpreadsheet, File, Clock } from 'lucide-react';
// --- Types ---
interface Report {
  id: string;
  name: string;
  type: string;
  date: string;
  period: string;
  format: string;
  status: 'Completato' | 'In corso' | 'Errore' | 'Pianificato';
}
// --- Mock Data ---
const INITIAL_REPORTS: Report[] = [{
  id: '1',
  name: 'Report ESG Q1 2026',
  type: 'ESG',
  date: '25 Mar 2026',
  period: 'Gen-Mar 2026',
  format: 'PDF',
  status: 'Completato'
}, {
  id: '2',
  name: 'Analisi Costi Marzo',
  type: 'Costi',
  date: '28 Mar 2026',
  period: 'Mar 2026',
  format: 'Excel',
  status: 'Completato'
}, {
  id: '3',
  name: 'Utilizzo Servizi Q1',
  type: 'Utilizzo Servizi',
  date: '29 Mar 2026',
  period: 'Gen-Mar 2026',
  format: 'PDF',
  status: 'In corso'
}, {
  id: '4',
  name: 'Compliance AI Act Annual',
  type: 'Compliance AI Act',
  date: '20 Mar 2026',
  period: '2025',
  format: 'PDF',
  status: 'Completato'
}, {
  id: '5',
  name: 'Report GDPR Trimestrale',
  type: 'GDPR',
  date: '15 Mar 2026',
  period: 'Gen-Mar 2026',
  format: 'PDF',
  status: 'Completato'
}, {
  id: '6',
  name: 'Performance Mensile Feb',
  type: 'Performance',
  date: '01 Mar 2026',
  period: 'Feb 2026',
  format: 'CSV',
  status: 'Completato'
}, {
  id: '7',
  name: 'Report Costi Febbraio',
  type: 'Costi',
  date: '28 Feb 2026',
  period: 'Feb 2026',
  format: 'Excel',
  status: 'Completato'
}, {
  id: '8',
  name: 'ESG Annual 2025',
  type: 'ESG',
  date: '15 Gen 2026',
  period: '2025',
  format: 'PDF',
  status: 'Completato'
}];
const REPORT_TYPES = [{
  id: 'ESG',
  icon: Leaf,
  title: 'ESG',
  desc: "Impatto ambientale e sostenibilità dell'infrastruttura AI"
}, {
  id: 'Costi',
  icon: Coins,
  title: 'Costi',
  desc: 'Analisi dettagliata dei costi per servizio e provider'
}, {
  id: 'Utilizzo Servizi',
  icon: Activity,
  title: 'Utilizzo Servizi',
  desc: 'Metriche di utilizzo, volumi e performance dei servizi'
}, {
  id: 'Compliance AI Act',
  icon: Shield,
  title: 'Compliance AI Act',
  desc: 'Stato di conformità rispetto al regolamento EU AI Act'
}, {
  id: 'GDPR',
  icon: FileText,
  title: 'GDPR',
  desc: 'Conformità al trattamento dati personali nei servizi AI'
}, {
  id: 'Performance',
  icon: TrendingUp,
  title: 'Performance',
  desc: 'Analisi delle performance e tempi di risposta'
}];
const THEMES_BY_TYPE: Record<string, string[]> = {
  ESG: ['Emissioni CO₂', 'Consumo energetico', 'Confronto provider', 'Suggerimenti ottimizzazione', 'Trend storico'],
  Costi: ['Breakdown per servizio', 'Breakdown per provider', 'Anomalie rilevate', 'Previsioni', 'Confronto periodi'],
  'Utilizzo Servizi': ['Volumi di richieste', 'Utenti attivi', 'Error rate', 'Latenza media', 'Distribuzione oraria'],
  'Compliance AI Act': ['Classificazione rischio', 'Trasparenza', 'Sorveglianza umana', 'Robustezza tecnica', 'Documentazione'],
  GDPR: ['Trattamento PII', 'Ritenzione dati', "Diritto all'oblio", 'Consensi', 'Data Breach'],
  Performance: ['Tempo di risposta', 'Throughput', 'Uptime', 'Errori per modello', 'Degrado prestazioni']
};
const SERVICES = ['Customer Assistant', 'Smart Matcher', 'Content Moderator', 'AI Tutor'];
const FORMAT_OPTIONS = [{
  id: 'PDF',
  icon: File,
  label: 'PDF',
  desc: 'Documento formattato, ideale per condivisione'
}, {
  id: 'Excel',
  icon: FileSpreadsheet,
  label: 'Excel',
  desc: 'Foglio di calcolo con dati dettagliati'
}, {
  id: 'CSV',
  icon: FileText,
  label: 'CSV',
  desc: 'Dati grezzi per analisi personalizzate'
}];
// --- Helpers ---
function getStatusBadge(status: string) {
  switch (status) {
    case 'Completato':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-4222" data-mp="1mpipomboh81p">
          Completato
        </span>;
    case 'In corso':
      return <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-4223" data-mp="1pspy6xqlh6lw">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" data-id="element-4224" data-mp="1fuijymnox2nl" />
          In corso
        </span>;
    case 'Errore':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200" data-id="element-4225" data-mp="1kripmwgmo3e6">
          Errore
        </span>;
    case 'Pianificato':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-4226" data-mp="1bui36vd4cfdd">
          Pianificato
        </span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-gray-50 text-gray-700 border border-gray-200" data-id="element-4227" data-mp="1209d0ebk8yid">
          {status}
        </span>;
  }
}
function getTypeBadge(type: string) {
  const colors: Record<string, string> = {
    ESG: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Costi: 'bg-blue-50 text-blue-700 border-blue-200',
    'Utilizzo Servizi': 'bg-purple-50 text-purple-700 border-purple-200',
    'Compliance AI Act': 'bg-amber-50 text-amber-700 border-amber-200',
    GDPR: 'bg-red-50 text-red-700 border-red-200',
    Performance: 'bg-cyan-50 text-cyan-700 border-cyan-200'
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${colors[type] || 'bg-gray-50 text-gray-700 border-gray-200'}`} data-id="element-4228" data-mp="sdydr8co4qzg">
      {type}
    </span>;
}
// --- Report content helpers ---
function getReportSummary(type: string): string {
  const summaries: Record<string, string> = {
    ESG: "L'infrastruttura AI ha generato 124 kg di CO₂ nel periodo analizzato, con una riduzione del 15% rispetto al trimestre precedente. Il routing dinamico verso modelli SLM ha contribuito al risparmio di 45 kg di emissioni. Si raccomanda l'adozione di provider con data center alimentati da energie rinnovabili per un ulteriore miglioramento.",
    Costi: 'La spesa complessiva per i servizi AI nel periodo è stata di €2.435, con un incremento del 17% rispetto alla media. Il servizio Customer Assistant rappresenta il 42% della spesa totale. Sono state identificate opportunità di ottimizzazione per un risparmio stimato di €380/mese attraverso il passaggio a modelli più efficienti.',
    GDPR: "L'analisi di conformità GDPR evidenzia un livello di compliance del 96%. Tutti i servizi implementano correttamente il mascheramento PII. Sono stati identificati 2 punti di attenzione relativi alla ritenzione dei dati di conversazione che superano il periodo previsto dalla policy aziendale.",
    'Compliance AI Act': 'La valutazione di conformità al regolamento EU AI Act classifica tutti i servizi attivi come rischio limitato o minimo. Il servizio Customer Assistant richiede documentazione aggiuntiva sulla trasparenza. Il framework di sorveglianza umana è implementato correttamente su tutti i servizi ad alto volume.',
    Performance: "Le performance complessive dei servizi AI mostrano un uptime del 99.7% nel periodo analizzato. Il tempo medio di risposta è di 1.2 secondi, con picchi di 3.8 secondi nelle ore di punta. Il servizio Smart Matcher ha registrato un degrado del 12% nelle performance durante l'ultimo aggiornamento del modello.",
    'Utilizzo Servizi': "Nel periodo analizzato, i servizi AI hanno gestito complessivamente 512.000 richieste con un tasso di successo del 98.3%. Il servizio Customer Assistant è il più utilizzato con il 45% del volume totale. Si registra una crescita del 22% nell'utilizzo rispetto al periodo precedente."
  };
  return summaries[type] || summaries['Utilizzo Servizi'];
}
function getReportMetrics(type: string) {
  const m: Record<string, Array<{
    label: string;
    value: string;
    sub: string;
  }>> = {
    ESG: [{
      label: 'CO₂ Emessa',
      value: '124 kg',
      sub: '-15% vs Q4'
    }, {
      label: 'Energia Consumata',
      value: '890 kWh',
      sub: '-8% vs Q4'
    }, {
      label: 'CO₂ Risparmiata',
      value: '45 kg',
      sub: 'Routing SLM'
    }, {
      label: 'Eco Score',
      value: 'B+',
      sub: 'In miglioramento'
    }],
    Costi: [{
      label: 'Spesa Totale',
      value: '€2.435',
      sub: '+17% vs media'
    }, {
      label: 'Costo Medio/Req',
      value: '€0.0047',
      sub: '-3% vs Q4'
    }, {
      label: 'Servizio Top',
      value: 'Cust. Assist.',
      sub: '42% del totale'
    }, {
      label: 'Risparmio Possibile',
      value: '€380/m',
      sub: 'Ottimizzazione'
    }],
    GDPR: [{
      label: 'Compliance',
      value: '96%',
      sub: '+2% vs Q4'
    }, {
      label: 'PII Masked',
      value: '100%',
      sub: 'Tutti i servizi'
    }, {
      label: 'Punti Attenzione',
      value: '2',
      sub: 'Ritenzione dati'
    }, {
      label: 'Data Breach',
      value: '0',
      sub: 'Nessun incidente'
    }]
  };
  return m[type] || [{
    label: 'Richieste Totali',
    value: '512K',
    sub: '+22% vs prec.'
  }, {
    label: 'Tasso Successo',
    value: '98.3%',
    sub: 'Stabile'
  }, {
    label: 'Tempo Medio',
    value: '1.2s',
    sub: '-0.3s vs Q4'
  }, {
    label: 'Servizi Attivi',
    value: '4',
    sub: 'Tutti operativi'
  }];
}
function getServiceRows(type: string) {
  const valCol = type === 'ESG' ? 'CO₂ (kg)' : type === 'Costi' ? 'Costo (€)' : 'Richieste';
  const rows = [{
    name: 'Customer Assistant',
    val: type === 'ESG' ? '52' : type === 'Costi' ? '€1.024' : '230K',
    change: type === 'Costi' ? '+17%' : '-12%',
    status: type === 'Costi' ? 'Attenzione' : 'Nella norma'
  }, {
    name: 'Smart Matcher',
    val: type === 'ESG' ? '38' : type === 'Costi' ? '€680' : '142K',
    change: '-8%',
    status: 'Nella norma'
  }, {
    name: 'Content Moderator',
    val: type === 'ESG' ? '22' : type === 'Costi' ? '€456' : '98K',
    change: '-5%',
    status: 'Nella norma'
  }, {
    name: 'AI Tutor',
    val: type === 'ESG' ? '12' : type === 'Costi' ? '€275' : '42K',
    change: '-22%',
    status: 'Ottimale'
  }];
  return {
    valCol,
    rows
  };
}
function getRecommendations(type: string) {
  const recs: Record<string, string[]> = {
    ESG: ['Valutare la migrazione del servizio Customer Assistant verso un provider con data center alimentati da energie rinnovabili.', 'Estendere il routing dinamico SLM a tutti i servizi per massimizzare il risparmio energetico.', 'Pianificare una revisione trimestrale delle configurazioni per allinearsi alle best practice aggiornate.'],
    Costi: ['Passare il servizio Customer Assistant al modello GPT-4o-mini per le query semplici, con risparmio stimato del 40%.', 'Implementare budget alert automatici per prevenire sforamenti di spesa sui singoli servizi.', 'Pianificare una revisione trimestrale delle configurazioni per allinearsi alle best practice aggiornate.']
  };
  return recs[type] || ['Implementare un sistema di caching per le richieste ripetute, riducendo il carico del 15%.', 'Monitorare il degrado delle performance durante gli aggiornamenti dei modelli con rollback automatico.', 'Pianificare una revisione trimestrale delle configurazioni per allinearsi alle best practice aggiornate.'];
}
// --- A4 Page wrapper for PDF preview ---
function A4Page({
  children,
  pageNum,
  totalPages
}: {
  children: React.ReactNode;
  pageNum: number;
  totalPages: number;
}) {
  return <div className="bg-white shadow-lg border border-gray-200 mx-auto mb-6 relative" style={{
    width: '595px',
    minHeight: '842px',
    padding: '48px 56px 64px 56px'
  }} data-id="element-4229" data-mp="3mzhqregijntq">
      {children}
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-between px-14" data-id="element-4230" data-mp="s9poocmm33i">
        <span className="text-[9px] text-gray-300" data-id="element-4231" data-mp="178c078hvlpwk">
          Confidenziale — Uso interno
        </span>
        <span className="text-[9px] text-gray-400" data-id="element-4232" data-mp="1c7g7ftl8pdk9">
          Pagina {pageNum} di {totalPages}
        </span>
      </div>
    </div>;
}
// --- Excel preview ---
function ExcelPreview({
  report
}: {
  report: Report;
}) {
  const {
    valCol,
    rows
  } = getServiceRows(report.type);
  const metrics = getReportMetrics(report.type);
  return <div className="bg-white border border-gray-300 rounded-lg overflow-hidden shadow-lg mx-auto" style={{
    maxWidth: '800px'
  }} data-id="element-4233" data-mp="wgfid83zcet3">
      {/* Toolbar */}
      <div className="bg-[#217346] px-4 py-2 flex items-center gap-3" data-id="element-4234" data-mp="3st3v4t7rprg">
        <FileSpreadsheet className="w-4 h-4 text-white" data-id="element-4235" data-mp="3t2xd2dgwf1w" />
        <span className="text-[12px] text-white font-medium" data-id="element-4236" data-mp="3uzvvtygvm02q">
          {report.name}.xlsx
        </span>
      </div>
      {/* Formula bar */}
      <div className="bg-gray-100 border-b border-gray-300 px-3 py-1.5 flex items-center gap-2" data-id="element-4237" data-mp="dkpdnd4nx4bi">
        <span className="text-[10px] font-mono text-gray-500 bg-white border border-gray-300 px-2 py-0.5 rounded" data-id="element-4238" data-mp="8pt95di8oohi">
          A1
        </span>
        <span className="text-[10px] font-mono text-gray-600" data-id="element-4239" data-mp="3c94qd800ettm">
          = {report.name}
        </span>
      </div>
      {/* Sheet content */}
      <div className="overflow-x-auto" data-id="element-4240" data-mp="1rozgtlkh4jgg">
        <table className="w-full border-collapse" data-id="element-4241" data-mp="1wh0rx45wo44w">
          <thead data-id="element-4242" data-mp="1wiveso0qm6bm">
            <tr className="bg-gray-50" data-id="element-4243" data-mp="1wjdig5pqbmtk">
              <th className="w-10 border border-gray-200 bg-gray-100 text-[9px] text-gray-500 text-center py-1" data-id="element-4244" data-mp="1hswi6potz2y9"></th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[120px]" data-id="element-4245" data-mp="1msiwz3kb4r96">
                A
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4246" data-mp="2fgohpqbd2g90">
                B
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4247" data-mp="21iq80y7zj7zu">
                C
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4248" data-mp="pzx6cfjn3uu4">
                D
              </th>
              <th className="border border-gray-200 bg-gray-100 text-[10px] text-gray-600 font-medium text-center py-1 px-2 min-w-[100px]" data-id="element-4249" data-mp="1aaq7szkilt6j">
                E
              </th>
            </tr>
          </thead>
          <tbody data-id="element-4250" data-mp="10kohs28lq0oz">
            {/* Title row */}
            <tr data-id="element-4251" data-mp="vlbz7torjfs7">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4252" data-mp="vltschg4pdvw">
                1
              </td>
              <td colSpan={5} className="border border-gray-200 py-2 px-3 text-[12px] font-bold text-[#1A202C] bg-blue-50" data-id="element-4253" data-mp="1t6q42mkizivx">
                {report.name}
              </td>
            </tr>
            <tr data-id="element-4254" data-mp="1j8tif0typ5jk">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4255" data-mp="1jcpnnojsmr7w">
                2
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-500" data-id="element-4256" data-mp="7vpyv2wnmuim">
                Periodo
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4257" data-mp="3sba9nunh5tk">
                {report.period}
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-500" data-id="element-4258" data-mp="2jijrir42ni4q">
                Data
              </td>
              <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4259" data-mp="2ogv1r2ya3unh">
                {report.date}
              </td>
              <td className="border border-gray-200" data-id="element-4260" data-mp="2taphjvarjpha"></td>
            </tr>
            <tr data-id="element-4261" data-mp="2y6xsewhxgwin">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4262" data-mp="2y7fy0jqhcn38">
                3
              </td>
              <td colSpan={5} className="border border-gray-200" data-id="element-4263" data-mp="377vxzj80lc3c"></td>
            </tr>
            {/* Metrics header */}
            <tr data-id="element-4264" data-mp="3c6fkplufmrwr">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4265" data-mp="3c6yrq7ijnvw0">
                4
              </td>
              <td colSpan={5} className="border border-gray-200 py-1.5 px-3 text-[11px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4266" data-mp="1hthfvhri4ngt">
                Metriche Principali
              </td>
            </tr>
            {metrics.map((m, i) => <tr key={i} data-id="element-4267" data-mp="2g5tmn5b15jib" data-mpi={i}>
                <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4268" data-mp="2g6lj0icgbl9n">
                  {5 + i}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-600" data-id="element-4269" data-mp="2p68c8gv11krm">
                  {m.label}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] font-medium text-[#1A202C]" data-id="element-4270" data-mp="341y0ymrlkc01">
                  {m.value}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-500" data-id="element-4271" data-mp="3cvb38fbjwpys">
                  {m.sub}
                </td>
                <td className="border border-gray-200" data-id="element-4272" data-mp="38v8zm0m99mgv"></td>
                <td className="border border-gray-200" data-id="element-4273" data-mp="3mth4cupj9s2l"></td>
              </tr>)}
            <tr data-id="element-4274" data-mp="3mre3tgqr8gjm">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4275" data-mp="3hunm6ozaxcw3">
                {9}
              </td>
              <td colSpan={5} className="border border-gray-200" data-id="element-4276" data-mp="5ccunc8l0tap"></td>
            </tr>
            {/* Service detail header */}
            <tr data-id="element-4277" data-mp="a81wjm82qma6">
              <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4278" data-mp="3ri9qrz7af088">
                10
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4279" data-mp="ani1s5po7ki">
                Servizio
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4280" data-mp="3ngoae3vjo3ta">
                {valCol}
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4281" data-mp="38tbg9qro8pra">
                Variazione
              </td>
              <td className="border border-gray-200 py-1.5 px-3 text-[10px] font-bold text-[#1A202C] bg-gray-50" data-id="element-4282" data-mp="2zqk7xzu3ea3v">
                Stato
              </td>
              <td className="border border-gray-200 bg-gray-50" data-id="element-4283" data-mp="33urtxp961bye"></td>
            </tr>
            {rows.map((row, i) => <tr key={i} data-id="element-4284" data-mp="3k5u1vxt4qg2l" data-mpi={i}>
                <td className="border border-gray-200 bg-gray-50 text-[9px] text-gray-400 text-center py-1" data-id="element-4285" data-mp="3f8k3hsnriu2m">
                  {11 + i}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4286" data-mp="3ac3fcygkcfpj">
                  {row.name}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4287" data-mp="319u60bx16sdq">
                  {row.val}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4288" data-mp="2wh12tsh7ns3r">
                  {row.change}
                </td>
                <td className="border border-gray-200 py-1 px-3 text-[10px] text-gray-700" data-id="element-4289" data-mp="2mko8qthr7c8h">
                  {row.status}
                </td>
                <td className="border border-gray-200" data-id="element-4290" data-mp="2hk1n1hc7gsy7"></td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Sheet tabs */}
      <div className="bg-gray-100 border-t border-gray-300 px-3 py-1.5 flex items-center gap-1" data-id="element-4291" data-mp="t1pcc8eovpuy">
        <span className="text-[10px] font-medium text-[#217346] bg-white border border-gray-300 px-3 py-1 rounded-t" data-id="element-4292" data-mp="x3ajdd4jl9x8">
          Riepilogo
        </span>
        <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 px-3 py-1 rounded-t" data-id="element-4293" data-mp="123orswa22lvs">
          Dettaglio Servizi
        </span>
        <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-200 px-3 py-1 rounded-t" data-id="element-4294" data-mp="9dq1yyo1ekq2">
          Dati Grezzi
        </span>
      </div>
    </div>;
}
// --- CSV preview ---
function CsvPreview({
  report
}: {
  report: Report;
}) {
  const {
    valCol,
    rows
  } = getServiceRows(report.type);
  const metrics = getReportMetrics(report.type);
  const lines = [`# ${report.name}`, `# Periodo: ${report.period}`, `# Data: ${report.date}`, `# Tipo: ${report.type}`, '', '# Metriche Principali', 'Metrica,Valore,Note', ...metrics.map(m => `${m.label},${m.value},${m.sub}`), '', '# Dettaglio per Servizio', `Servizio,${valCol},Variazione,Stato`, ...rows.map(r => `${r.name},${r.val},${r.change},${r.status}`)];
  return <div className="bg-[#1e1e1e] rounded-lg overflow-hidden shadow-lg mx-auto border border-gray-700" style={{
    maxWidth: '700px'
  }} data-id="element-4295" data-mp="34njt5ac6ekcf">
      <div className="bg-[#2d2d2d] px-4 py-2 flex items-center gap-2 border-b border-gray-700" data-id="element-4296" data-mp="39ld0i6p7tkea">
        <FileText className="w-3.5 h-3.5 text-gray-400" data-id="element-4297" data-mp="3elr7dlf2yoaq" />
        <span className="text-[11px] text-gray-300 font-mono" data-id="element-4298" data-mp="2gzjw5so9txpe">
          {report.name.replace(/\s+/g, '_').toLowerCase()}.csv
        </span>
      </div>
      <div className="p-4 overflow-x-auto" data-id="element-4299" data-mp="2lviotsgmu1vm">
        <pre className="text-[11px] font-mono leading-relaxed" data-id="element-4300" data-mp="2qwogro2gxl43">
          {lines.map((line, i) => <div key={i} className="flex" data-id="element-4301" data-mp="2vo6msqjcmbvx" data-mpi={i}>
              <span className="text-gray-600 w-8 text-right mr-4 select-none flex-shrink-0" data-id="element-4302" data-mp="2vozj0xkolizt">
                {i + 1}
              </span>
              <span className={line.startsWith('#') ? 'text-green-400' : line.includes(',') ? 'text-gray-300' : 'text-gray-500'} data-id="element-4303" data-mp="2373yz8i1wtwe">
                {line}
              </span>
            </div>)}
        </pre>
      </div>
    </div>;
}
// --- PDF preview (A4 pages) ---
function PdfPreview({
  report
}: {
  report: Report;
}) {
  const summary = getReportSummary(report.type);
  const metrics = getReportMetrics(report.type);
  const {
    valCol,
    rows
  } = getServiceRows(report.type);
  const recs = getRecommendations(report.type);
  const typeIcon = REPORT_TYPES.find(rt => rt.id === report.type);
  return <div className="flex flex-col items-center" data-id="element-4304" data-mp="3ssb0n8yshk2g">
      {/* Page 1: Cover */}
      <A4Page pageNum={1} totalPages={3} data-id="element-4305" data-mp="3sq80vx7u4ckj">
        <div className="flex flex-col h-full justify-between" style={{
        minHeight: '700px'
      }} data-id="element-4306" data-mp="3opo2yrfft4xc">
          <div data-id="element-4307" data-mp="3jsfp331bnn1w">
            <div className="flex items-center justify-between mb-16" data-id="element-4308" data-mp="3jsxryiihozis">
              <div className="flex items-center gap-2" data-id="element-4309" data-mp="3esjkb1ksvrla">
                <div className="w-8 h-8 rounded bg-[#2A3B5C] flex items-center justify-center" data-id="element-4310" data-mp="3eu4bch7wosee">
                  <span className="text-white text-[10px] font-bold" data-id="element-4311" data-mp="39vjcal4e4erj">AI</span>
                </div>
                <span className="text-[11px] font-medium text-gray-400" data-id="element-4312" data-mp="307xi84u169tn">
                  AI Governance Platform
                </span>
              </div>
              <span className="text-[10px] text-gray-400" data-id="element-4313" data-mp="2r67sr4jplkyi">CONFIDENZIALE</span>
            </div>
            <div className="mt-20" data-id="element-4314" data-mp="2v7j8mics6rti">
              <div className="w-12 h-1 bg-[#00B4D8] rounded mb-6" data-id="element-4315" data-mp="2v8kmsvp3fufg" />
              <p className="text-[11px] text-[#00B4D8] uppercase tracking-widest font-medium mb-3" data-id="element-4316" data-mp="3ixdjwvhgc8bn">
                Report {report.type}
              </p>
              <h1 className="text-[28px] font-bold text-[#1A202C] leading-tight mb-4" data-id="element-4317" data-mp="3a40kdaovu8gl">
                {report.name}
              </h1>
              <div className="w-16 h-0.5 bg-gray-200 mb-6" data-id="element-4318" data-mp="3e4ar4pki12wi" />
              <div className="space-y-2 text-[12px] text-gray-500" data-id="element-4319" data-mp="5pk7r0e32q3t">
                <p data-id="element-4320" data-mp="5o93kzgq7gc6">
                  Periodo di riferimento:{' '}
                  <span className="text-[#1A202C] font-medium" data-id="element-4321" data-mp="ahamdik20011">
                    {report.period}
                  </span>
                </p>
                <p data-id="element-4322" data-mp="1v4bftpelqyjn">
                  Data di generazione:{' '}
                  <span className="text-[#1A202C] font-medium" data-id="element-4323" data-mp="1q6tdihv3bhkm">
                    {report.date}
                  </span>
                </p>
                <p data-id="element-4324" data-mp="1zxebq46uqk30">
                  Formato:{' '}
                  <span className="text-[#1A202C] font-medium" data-id="element-4325" data-mp="1zypf42wdxptl">
                    {report.format}
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-auto" data-id="element-4326" data-mp="17a0mytdb4utz">
            <p className="text-[10px] text-gray-400" data-id="element-4327" data-mp="1m6izqj123mv7">
              Generato automaticamente dalla piattaforma AI Governance · SIAE
            </p>
          </div>
        </div>
      </A4Page>

      {/* Page 2: Executive Summary + Metrics */}
      <A4Page pageNum={2} totalPages={3} data-id="element-4328" data-mp="2xjlfjbvc08eb">
        <div data-id="element-4329" data-mp="2sm22gfade1ny">
          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4330" data-mp="2sml92zukl3mo">
            1. Executive Summary
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4331" data-mp="125zu4r6mlfro" />
          <p className="text-[11px] text-gray-600 leading-[1.8] mb-8" data-id="element-4332" data-mp="1741ergjp9n59">
            {summary}
          </p>

          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4333" data-mp="1b5wa84ey96b4">
            2. Metriche Principali
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4334" data-mp="1g4zhlviejom9" />
          <div className="grid grid-cols-2 gap-3 mb-8" data-id="element-4335" data-mp="1l3tzmv8jp95b">
            {metrics.map((m, i) => <div key={i} className="border border-gray-200 rounded-lg p-3" data-id="element-4336" data-mp="1l1pygl216o8q" data-mpi={i}>
                <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1" data-id="element-4337" data-mp="1q1ca4q27nwff">
                  {m.label}
                </p>
                <p className="text-[18px] font-bold text-[#1A202C]" data-id="element-4338" data-mp="1uvzss8aj9wm3">
                  {m.value}
                </p>
                <p className="text-[9px] text-gray-400 mt-0.5" data-id="element-4339" data-mp="23uk3on9ur1zr">{m.sub}</p>
              </div>)}
          </div>

          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4340" data-mp="28svdiy05dchz">
            3. Dettaglio per Servizio
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4341" data-mp="2iyd600aajo5m" />
          <table className="w-full mb-4" data-id="element-4342" data-mp="2dyiafjfkmae8">
            <thead data-id="element-4343" data-mp="2dxgx18b31btc">
              <tr className="border-b-2 border-gray-200" data-id="element-4344" data-mp="2e0bldax7vgs1">
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4345" data-mp="290725d4xc7y4">
                  Servizio
                </th>
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4346" data-mp="31ntgngfowwzr">
                  {valCol}
                </th>
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4347" data-mp="2wp9v3h50dn7x">
                  Var.
                </th>
                <th className="text-left text-[9px] font-semibold text-gray-500 uppercase tracking-wider pb-2" data-id="element-4348" data-mp="2nwy4nv9w9zry">
                  Stato
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4349" data-mp="3fnufyrgb3wd4">
              {rows.map((row, i) => <tr key={i} className="border-b border-gray-100" data-id="element-4350" data-mp="3fmswbyl861js" data-mpi={i}>
                  <td className="py-2 text-[10px] font-medium text-[#1A202C]" data-id="element-4351" data-mp="1dpiovmoztjvp">
                    {row.name}
                  </td>
                  <td className="py-2 text-[10px] text-gray-600" data-id="element-4352" data-mp="1iolsd2pbri1k">{row.val}</td>
                  <td className="py-2 text-[10px]" data-id="element-4353" data-mp="13z7ilthu6yln">
                    <span className={row.change.startsWith('+') ? 'text-red-600' : 'text-emerald-600'} data-id="element-4354" data-mp="13yei3a9h15by">
                      {row.change}
                    </span>
                  </td>
                  <td className="py-2 text-[10px] text-gray-600" data-id="element-4355" data-mp="v1ngyjg2b0ai">
                    {row.status}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </A4Page>

      {/* Page 3: Recommendations + Footer */}
      <A4Page pageNum={3} totalPages={3} data-id="element-4356" data-mp="pzodl9kbo3dk">
        <div data-id="element-4357" data-mp="q2k9kscm0hzd">
          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4358" data-mp="q2aqlopzfb36">
            4. Raccomandazioni
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4359" data-mp="2ke8poxukuqlc" />
          <div className="space-y-4 mb-12" data-id="element-4360" data-mp="2kceh8j0hsqy5">
            {recs.map((rec, i) => <div key={i} className="flex items-start gap-3" data-id="element-4361" data-mp="8s6tsnshu6r3" data-mpi={i}>
                <div className="w-5 h-5 rounded-full bg-[#00B4D8]/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4362" data-mp="3sc4upsa3r8o">
                  <span className="text-[9px] font-bold text-[#00B4D8]" data-id="element-4363" data-mp="ii083wk0szz1">
                    {i + 1}
                  </span>
                </div>
                <p className="text-[11px] text-gray-600 leading-[1.8]" data-id="element-4364" data-mp="do5i6bdtnori">{rec}</p>
              </div>)}
          </div>

          <h2 className="text-[16px] font-bold text-[#1A202C] mb-1" data-id="element-4365" data-mp="ng1ouatz4n7c">
            5. Metodologia
          </h2>
          <div className="w-8 h-0.5 bg-[#00B4D8] rounded mb-4" data-id="element-4366" data-mp="11fkkw59ig83a" />
          <p className="text-[11px] text-gray-600 leading-[1.8] mb-8" data-id="element-4367" data-mp="11dqavlzs20dz">
            I dati presentati in questo report sono stati raccolti
            automaticamente dalla piattaforma AI Governance Engine. Le metriche
            sono calcolate sulla base dei log di utilizzo, dei costi fatturati
            dai provider e delle analisi di conformità eseguite in tempo reale.
            Le raccomandazioni sono generate attraverso l'analisi comparativa
            con le best practice di settore e i benchmark interni.
          </p>

          <div className="border-t border-gray-200 pt-6 mt-12" data-id="element-4368" data-mp="2m470ntsz0hkw">
            <div className="flex items-center justify-between" data-id="element-4369" data-mp="2m6thkk1r49fs">
              <div data-id="element-4370" data-mp="2r2iba6r3aw46">
                <p className="text-[10px] text-gray-400 mb-1" data-id="element-4371" data-mp="2r31s1k507egm">
                  Documento generato automaticamente
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-4372" data-mp="1yew8h0porvid">
                  AI Governance Platform — SIAE © 2026
                </p>
              </div>
              <div className="text-right" data-id="element-4373" data-mp="23co0duqbzhmk">
                <p className="text-[10px] text-gray-400 mb-1" data-id="element-4374" data-mp="28dbwuvxbwqo6">
                  Classificazione: Confidenziale
                </p>
                <p className="text-[10px] text-gray-400" data-id="element-4375" data-mp="3escmykzoaemu">
                  Distribuzione: Uso interno
                </p>
              </div>
            </div>
          </div>
        </div>
      </A4Page>
    </div>;
}
// --- Report Preview Modal ---
function ReportPreviewModal({
  report,
  onClose
}: {
  report: Report;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-4376" data-mp="138euysvhmtsv">
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl" data-id="element-4377" data-mp="tg945mgz2if9">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0" data-id="element-4378" data-mp="oipoqj8jfzlq">
          <div className="flex items-center gap-3" data-id="element-4379" data-mp="ke9nwwds7idd">
            <div className="w-9 h-9 rounded-lg bg-[#00B4D8]/10 flex items-center justify-center" data-id="element-4380" data-mp="ffok6mzbmgk6">
              {report.format === 'Excel' ? <FileSpreadsheet className="w-4.5 h-4.5 text-[#217346]" data-id="element-4381" data-mp="aiy4w1vcbozt" /> : report.format === 'CSV' ? <FileText className="w-4.5 h-4.5 text-gray-500" data-id="element-4382" data-mp="29tfyw0wibqmh" /> : <File className="w-4.5 h-4.5 text-red-500" data-id="element-4383" data-mp="24vek3s646dgt" />}
            </div>
            <div data-id="element-4384" data-mp="24w7kmbehc6qi">
              <h2 className="text-[15px] font-heading font-semibold text-[#1A202C]" data-id="element-4385" data-mp="24tl7lw8tkz0w">
                {report.name}
              </h2>
              <div className="flex items-center gap-2 mt-0.5" data-id="element-4386" data-mp="2q70fs2090wcb">
                {getTypeBadge(report.type)}
                <span className="text-[10px] text-gray-400" data-id="element-4387" data-mp="34u3cw7knt2z4">
                  {report.date} · {report.period}
                </span>
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-600" data-id="element-4388" data-mp="2zwtpexgtz1dx">
                  {report.format}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-4389" data-mp="2l6vvs204tjhc">
            <button className="flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4390" data-mp="2l8gt1zdtzgiw">
              <Download className="w-3.5 h-3.5" data-id="element-4391" data-mp="fvk694ehq71" />
              Scarica
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all" data-id="element-4392" data-mp="3slh9ippufkpw">
              <X className="w-5 h-5" data-id="element-4393" data-mp="3xj8htq7y4at" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6" data-id="element-4394" data-mp="8vuh614s28rg">
          {report.format === 'PDF' && <PdfPreview report={report} data-id="element-4395" data-mp="3q85bjqyuxw7y" />}
          {report.format === 'Excel' && <ExcelPreview report={report} data-id="element-4396" data-mp="3v5x32jvlbgbm" />}
          {report.format === 'CSV' && <CsvPreview report={report} data-id="element-4397" data-mp="mosjk7fn114i" />}
        </div>
      </div>
    </div>;
}
// --- Main Component ---
export function ReportPage() {
  const [reports, setReports] = useState<Report[]>(INITIAL_REPORTS);
  // Preview modal state
  const [previewReport, setPreviewReport] = useState<Report | null>(null);
  // Wizard state
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  // Form state
  const [selectedType, setSelectedType] = useState('ESG');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('PDF');
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
  const handleOpenWizard = () => {
    setWizardStep(1);
    setSelectedType('ESG');
    setSelectedThemes(['Emissioni CO₂', 'Consumo energetico']);
    setDateFrom('');
    setDateTo('');
    setSelectedServices([...SERVICES]);
    setNotes('');
    setSelectedFormat('PDF');
    setIsGenerating(false);
    setProgress(0);
    setIsComplete(false);
    setIsWizardOpen(true);
  };
  const handleCloseWizard = () => {
    if (isComplete) {
      const newReport: Report = {
        id: Math.random().toString(36).substr(2, 9),
        name: `Nuovo Report ${selectedType}`,
        type: selectedType,
        date: new Date().toLocaleDateString('it-IT', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        period: dateFrom && dateTo ? `${dateFrom} / ${dateTo}` : 'Mese Corrente',
        format: selectedFormat,
        status: 'Completato'
      };
      setReports(prev => [newReport, ...prev]);
    }
    setIsWizardOpen(false);
  };
  const toggleTheme = (theme: string) => {
    setSelectedThemes(prev => prev.includes(theme) ? prev.filter(t => t !== theme) : [...prev, theme]);
  };
  const toggleService = (service: string) => {
    setSelectedServices(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };
  const startGeneration = () => {
    setWizardStep(4);
    setIsGenerating(true);
    setProgress(0);
    setProgressMessage('Raccolta dati in corso...');
    const duration = 3000;
    const interval = 30;
    const steps = duration / interval;
    let currentStep = 0;
    timerRef.current = setInterval(() => {
      currentStep++;
      const p = Math.floor(currentStep / steps * 100);
      setProgress(p);
      if (p < 25) {
        setProgressMessage('Raccolta dati in corso...');
      } else if (p < 50) {
        setProgressMessage('Analisi ed elaborazione metriche...');
      } else if (p < 75) {
        setProgressMessage('Generazione documento e formattazione...');
      } else if (p < 100) {
        setProgressMessage('Finalizzazione report...');
      }
      if (p >= 100) {
        if (timerRef.current) clearInterval(timerRef.current);
        setIsGenerating(false);
        setIsComplete(true);
        setProgressMessage('Report generato con successo!');
      }
    }, interval);
  };
  const currentThemes = THEMES_BY_TYPE[selectedType] || [];
  const stepLabels = ['Tipo', 'Configurazione', 'Formato', 'Generazione'];
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-4398" data-mp="2in9ceqp9obou">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-4399" data-mp="23xv3fjplrw9z">
        <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-4400" data-mp="28wofr8t5lh8k">
          Report
        </h1>
        <button onClick={handleOpenWizard} className="flex items-center gap-2 px-4 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-4401" data-mp="1uwwxuwzingpa">
          <Plus className="w-4 h-4" data-id="element-4402" data-mp="1l1bxt4e0cqi6" />
          <span className="text-[13px] font-medium" data-id="element-4403" data-mp="1l2dd5l223z5p">Genera Report</span>
        </button>
      </div>

      {/* Report Table */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-4404" data-mp="3fdjpm8v18r9q">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-4405" data-mp="3kcwa4myflm95">
          <h2 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-4406" data-mp="3ivpkpk1531zo">
            Report Generati
          </h2>
          <span className="text-[12px] text-gray-500" data-id="element-4407" data-mp="3dwcwovyj7ovi">
            {reports.length} report
          </span>
        </div>
        <div className="overflow-x-auto" data-id="element-4408" data-mp="38yuox8w93fp3">
          <table className="w-full" data-id="element-4409" data-mp="38zmhsbygqzbo">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-4410" data-mp="33zhzqhhwpwjc">
              <tr data-id="element-4411" data-mp="5h2za15l77u2">
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4412" data-mp="5hm72p1iwfud">
                  Nome Report
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4413" data-mp="kc8ais8urtv">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4414" data-mp="3mwsy7zvpslxq">
                  Data Creazione
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4415" data-mp="jgc464ocefiq">
                  Periodo
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4416" data-mp="rrkwbxyfqhtm">
                  Formato
                </th>
                <th className="px-6 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4417" data-mp="10peocwp62paz">
                  Stato
                </th>
                <th className="px-6 py-3 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider" data-id="element-4418" data-mp="1aom05fg96bvz">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-4419" data-mp="1jgxql1bd9zby">
              {reports.map(report => <tr key={report.id} className="hover:bg-gray-50 transition-colors" data-id="element-4420" data-mp="1jj0f1gwvfkek">
                  <td className="px-6 py-4" data-id="element-4421" data-mp="1ohv6f78wmhaa">
                    <div className="flex items-center gap-2.5" data-id="element-4422" data-mp="1tfdfcxmx8wia">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-4423" data-mp="1td0sc62b3vyd" />
                      <span className="text-[13px] font-medium text-[#1A202C]" data-id="element-4424" data-mp="1ye6ko2s21h7d">
                        {report.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-4425" data-mp="2epis7v2d71gx">{getTypeBadge(report.type)}</td>
                  <td className="px-6 py-4 text-[12px] text-gray-600" data-id="element-4426" data-mp="2soickqiugu7a">
                    {report.date}
                  </td>
                  <td className="px-6 py-4 text-[12px] text-gray-600" data-id="element-4427" data-mp="2nr9z338n5ecd">
                    {report.period}
                  </td>
                  <td className="px-6 py-4" data-id="element-4428" data-mp="1vzaq9gaeih2b">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700" data-id="element-4429" data-mp="1w0lu1g3ujotf">
                      {report.format}
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-4430" data-mp="24yo1ade6bdp5">{getStatusBadge(report.status)}</td>
                  <td className="px-6 py-4" data-id="element-4431" data-mp="1h93hjsa74cet">
                    <div className="flex items-center justify-end gap-1" data-id="element-4432" data-mp="1cbbga7nnvdxy">
                      <button title="Scarica" className="p-1.5 text-gray-400 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4433" data-mp="1c98u5ypmq4yi">
                        <Download className="w-4 h-4" data-id="element-4434" data-mp="3spwxy1yl5z83" />
                      </button>
                      <button title="Visualizza" onClick={() => setPreviewReport(report)} className="p-1.5 text-gray-400 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4435" data-mp="3ey8rae73w2yi">
                        <Eye className="w-4 h-4" data-id="element-4436" data-mp="bha0zmd2nibl" />
                      </button>
                      <button title="Elimina" className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" data-id="element-4437" data-mp="bf5m69dmwywj">
                        <Trash2 className="w-4 h-4" data-id="element-4438" data-mp="1jlxo81eh2ht" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== REPORT PREVIEW MODAL ===== */}
      {previewReport && <ReportPreviewModal report={previewReport} onClose={() => setPreviewReport(null)} data-id="element-4439" data-mp="2r8eetxqp8ges" />}

      {/* ===== WIZARD MODAL ===== */}
      {isWizardOpen && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-4440" data-mp="29ke3s2utz8cl">
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" data-id="element-4441" data-mp="2edh0gjuwmxx0">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between" data-id="element-4442" data-mp="24n4eqobcsypa">
              <div data-id="element-4443" data-mp="2scowx4zonrxi">
                <h2 className="text-[17px] font-heading font-semibold text-[#1A202C]" data-id="element-4444" data-mp="2scgmdjr18yph">
                  Genera Nuovo Report
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-4445" data-mp="2jb8y7ui8de94">
                  Step {wizardStep} di 4 — {stepLabels[wizardStep - 1]}
                </p>
              </div>
              <button onClick={() => setIsWizardOpen(false)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all" data-id="element-4446" data-mp="2nef5n5h2xbh6">
                <X className="w-5 h-5" data-id="element-4447" data-mp="1by6yeqqpdbz6" />
              </button>
            </div>

            {/* Step Indicator */}
            <div className="px-6 pt-4 pb-2" data-id="element-4448" data-mp="14cbjt4is39v2">
              <div className="flex items-center gap-2" data-id="element-4449" data-mp="zf1t7ljnks5n">
                {stepLabels.map((label, i) => <Fragment key={label} data-id="element-4450" data-mp="1ec2b6xc1l8qx" data-mpi={i}>
                    <div className="flex items-center gap-2" data-id="element-4451" data-mp="1eb11aw6risas">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${i + 1 < wizardStep ? 'bg-emerald-500 text-white' : i + 1 === wizardStep ? 'bg-[#00B4D8] text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-4452" data-mp="19d8xb3suffq8">
                        {i + 1 < wizardStep ? <CheckCircle2 className="w-4 h-4" data-id="element-4453" data-mp="vd7yrv01wkdx" /> : i + 1}
                      </div>
                      <span className={`text-[12px] font-medium hidden sm:inline ${i + 1 === wizardStep ? 'text-[#1A202C]' : 'text-gray-400'}`} data-id="element-4454" data-mp="qmht0gnpqfip">
                        {label}
                      </span>
                    </div>
                    {i < stepLabels.length - 1 && <div className={`flex-1 h-px ${i + 1 < wizardStep ? 'bg-emerald-300' : 'bg-gray-200'}`} data-id="element-4455" data-mp="3u2ovwkyb08qf" />}
                  </Fragment>)}
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-4456" data-mp="rlqh5v5xe2ro">
              {/* STEP 1: Report Type */}
              {wizardStep === 1 && <div data-id="element-4457" data-mp="11dcym98uwy2v">
                  <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4458" data-mp="11dw58tt2401l">
                    Seleziona il tipo di report
                  </h3>
                  <p className="text-[12px] text-gray-500 mb-5" data-id="element-4459" data-mp="4owzblfy3ack">
                    Scegli la categoria di report che desideri generare.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-4460" data-mp="dmqqyj2rlfte">
                    {REPORT_TYPES.map(rt => {
                const Icon = rt.icon;
                const isSelected = selectedType === rt.id;
                return <button key={rt.id} onClick={() => {
                  setSelectedType(rt.id);
                  const defaults = THEMES_BY_TYPE[rt.id] || [];
                  setSelectedThemes(defaults.slice(0, 2));
                }} className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#00B4D8] bg-[#00B4D8]/5 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-4461" data-mp="1p2p7t946p656">
                          <div className="flex items-center gap-2.5 mb-2" data-id="element-4462" data-mp="2zv6cnsy5k2yx">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-[#00B4D8]/10' : 'bg-gray-100'}`} data-id="element-4463" data-mp="2zt3ntc8qkfvs">
                              <Icon className={`w-4 h-4 ${isSelected ? 'text-[#00B4D8]' : 'text-gray-500'}`} data-id="element-4464" data-mp="2qu8gtxxvb1rf" />
                            </div>
                            <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-4465" data-mp="2lxhrek06attc">
                              {rt.title}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 leading-relaxed" data-id="element-4466" data-mp="k390n4zmtnp7">
                            {rt.desc}
                          </p>
                        </button>;
              })}
                  </div>
                </div>}

              {/* STEP 2: Configuration */}
              {wizardStep === 2 && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4467" data-mp="s2nfhaauwzrv">
                  {/* Left: Themes */}
                  <div data-id="element-4468" data-mp="d9tsl3t99hd6">
                    <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4469" data-mp="i60w6py6igry">
                      Temi e obiettivi
                    </h3>
                    <p className="text-[12px] text-gray-500 mb-4" data-id="element-4470" data-mp="4dugt62uodtk">
                      Seleziona gli argomenti da includere nel report.
                    </p>
                    <div className="space-y-2" data-id="element-4471" data-mp="3qn58ahbsxtp1">
                      {currentThemes.map(theme => <label key={theme} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedThemes.includes(theme) ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4472" data-mp="3vl6z5ofgyxb2">
                          <input type="checkbox" checked={selectedThemes.includes(theme)} onChange={() => toggleTheme(theme)} className="w-4 h-4 rounded border-gray-300 text-[#00B4D8] focus:ring-[#00B4D8]" data-id="element-4473" data-mp="3llzeeg6eo0dx" />
                          <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-4474" data-mp="2y1gkj8xy93n0">
                            {theme}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  {/* Right: Filters */}
                  <div className="space-y-5" data-id="element-4475" data-mp="3lwt056pmhia8">
                    <div data-id="element-4476" data-mp="3lvqdjazc2204">
                      <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4477" data-mp="3gyzt6bghwk8w">
                        Periodo
                      </h3>
                      <p className="text-[12px] text-gray-500 mb-3" data-id="element-4478" data-mp="3upmvsepdmua9">
                        Intervallo temporale del report.
                      </p>
                      <div className="grid grid-cols-2 gap-3" data-id="element-4479" data-mp="8iol3dag5iq9">
                        <div data-id="element-4480" data-mp="8gtzrxv9joln">
                          <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-4481" data-mp="3k3k3bndev0r">
                            Da
                          </label>
                          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12px] text-[#1A202C] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-4482" data-mp="2xdo4lknimwgq" />
                        </div>
                        <div data-id="element-4483" data-mp="3b52nlrhfaxns">
                          <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-4484" data-mp="3g2udkfyic9pc">
                            A
                          </label>
                          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12px] text-[#1A202C] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-4485" data-mp="3l3igo000ggzx" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-4486" data-mp="1zp4btfuv1xiv">
                      <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4487" data-mp="1zob9qs6ujw72">
                        Servizi da includere
                      </h3>
                      <p className="text-[12px] text-gray-500 mb-3" data-id="element-4488" data-mp="1ndjxu6go5oux">
                        Seleziona i servizi AI da analizzare.
                      </p>
                      <div className="space-y-2" data-id="element-4489" data-mp="1ice4c6f6pxkc">
                        {SERVICES.map(service => <label key={service} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${selectedServices.includes(service) ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-4490" data-mp="1dh8if7ls8iw9">
                            <input type="checkbox" checked={selectedServices.includes(service)} onChange={() => toggleService(service)} className="w-4 h-4 rounded border-gray-300 text-[#00B4D8] focus:ring-[#00B4D8]" data-id="element-4491" data-mp="214p2xo2t39cn" />
                            <span className="text-[13px] text-[#1A202C]" data-id="element-4492" data-mp="pr4hj7zzax8y">
                              {service}
                            </span>
                          </label>)}
                      </div>
                    </div>

                    <div data-id="element-4493" data-mp="2krwtd7vtdrf0">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-4494" data-mp="2psb44xosvfgq">
                        Note aggiuntive (opzionale)
                      </label>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Aggiungi eventuali note o richieste specifiche..." className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-[12px] text-[#1A202C] placeholder-gray-400 focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] resize-none" data-id="element-4495" data-mp="2bue6rz2iv510" />
                    </div>
                  </div>
                </div>}

              {/* STEP 3: Format & Summary */}
              {wizardStep === 3 && <div data-id="element-4496" data-mp="2zo5qa2s8fso9">
                  <h3 className="text-[14px] font-semibold text-[#1A202C] mb-1" data-id="element-4497" data-mp="3ngcrbf8wknu0">
                    Formato di esportazione
                  </h3>
                  <p className="text-[12px] text-gray-500 mb-5" data-id="element-4498" data-mp="3s8clss67eoj7">
                    Scegli il formato in cui generare il report.
                  </p>

                  <div className="grid grid-cols-3 gap-3 mb-8" data-id="element-4499" data-mp="1evqxxhzhdv1">
                    {FORMAT_OPTIONS.map(fmt => {
                const Icon = fmt.icon;
                const isSelected = selectedFormat === fmt.id;
                return <button key={fmt.id} onClick={() => setSelectedFormat(fmt.id)} className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-[#00B4D8] bg-[#00B4D8]/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4500" data-mp="a6nudo8lbmq4">
                          <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-[#00B4D8]' : 'text-gray-400'}`} data-id="element-4501" data-mp="3en8ip2nj58dq" />
                          <p className="text-[13px] font-semibold text-[#1A202C]" data-id="element-4502" data-mp="3sikdfa1oqyun">
                            {fmt.label}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-4503" data-mp="17tmr1yza8uo0">
                            {fmt.desc}
                          </p>
                        </button>;
              })}
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-5" data-id="element-4504" data-mp="2rtxalmazyrd7">
                    <h4 className="text-[13px] font-semibold text-[#1A202C] mb-4" data-id="element-4505" data-mp="2mzb705b5q33o">
                      Riepilogo Configurazione
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-[12px]" data-id="element-4506" data-mp="2dxk2h3scs2be">
                      <div data-id="element-4507" data-mp="3amjaqisxt43l">
                        <span className="text-gray-500" data-id="element-4508" data-mp="3alq0v8yolyh8">Tipo Report</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4509" data-mp="36knzoml7fccm">
                          {selectedType}
                        </p>
                      </div>
                      <div data-id="element-4510" data-mp="31s4lbnep0d6r">
                        <span className="text-gray-500" data-id="element-4511" data-mp="2wvwbmpj9lc6z">Formato</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4512" data-mp="2wtjv8gt5n9w1">
                          {selectedFormat}
                        </p>
                      </div>
                      <div data-id="element-4513" data-mp="3pih5bwn7q0dp">
                        <span className="text-gray-500" data-id="element-4514" data-mp="3pi79vtjef10m">Periodo</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4515" data-mp="rdfznms9m3y">
                          {dateFrom && dateTo ? `${dateFrom} — ${dateTo}` : 'Mese corrente'}
                        </p>
                      </div>
                      <div data-id="element-4516" data-mp="aqcnv3zq5dpw">
                        <span className="text-gray-500" data-id="element-4517" data-mp="eq39ktae93nk">Servizi</span>
                        <p className="font-medium text-[#1A202C] mt-0.5" data-id="element-4518" data-mp="en8qb5349ovq">
                          {selectedServices.length === SERVICES.length ? 'Tutti i servizi' : `${selectedServices.length} servizi`}
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-4519" data-mp="tfsqbwuyzg8y">
                        <span className="text-gray-500" data-id="element-4520" data-mp="ydkqtf9okaor">Temi selezionati</span>
                        <div className="flex flex-wrap gap-1.5 mt-1.5" data-id="element-4521" data-mp="yfnehsnd1rqb">
                          {selectedThemes.map(t => <span key={t} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#00B4D8]/10 text-[#00B4D8] border border-[#00B4D8]/20" data-id="element-4522" data-mp="12d335mnfrdfz">
                              {t}
                            </span>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* STEP 4: Generation */}
              {wizardStep === 4 && <div className="flex flex-col items-center justify-center py-10" data-id="element-4523" data-mp="38fyqn8er8gs7">
                  {!isComplete ? <>
                      <div className="w-16 h-16 rounded-full bg-[#00B4D8]/10 flex items-center justify-center mb-6" data-id="element-4524" data-mp="3n41twa1exedb">
                        <Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" data-id="element-4525" data-mp="3i60crunjrp4h" />
                      </div>
                      <h3 className="text-[16px] font-semibold text-[#1A202C] mb-2" data-id="element-4526" data-mp="3w3ynmq2nt3f8">
                        Generazione in corso...
                      </h3>
                      <p className="text-[13px] text-gray-500 mb-6" data-id="element-4527" data-mp="21t32ype79xw3">
                        {progressMessage}
                      </p>
                      <div className="w-full max-w-md" data-id="element-4528" data-mp="1wv1n0dc2meou">
                        <div className="flex items-center justify-between mb-2" data-id="element-4529" data-mp="cbxt78ohxezq">
                          <span className="text-[11px] text-gray-500" data-id="element-4530" data-mp="haiuzcjec6qa">
                            Progresso
                          </span>
                          <span className="text-[11px] font-medium text-[#1A202C]" data-id="element-4531" data-mp="3i2x0q5cqyw8">
                            {progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-4532" data-mp="3pp18hts3weha">
                          <div className="bg-[#00B4D8] h-2 rounded-full transition-all duration-100" style={{
                    width: `${progress}%`
                  }} data-id="element-4533" data-mp="3up5q5n4r3f93" />
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-6 text-[11px] text-gray-400" data-id="element-4534" data-mp="3kr81iyizbgfy">
                        <span className="flex items-center gap-1" data-id="element-4535" data-mp="1ey9g8e59qc4y">
                          <Clock className="w-3 h-3" data-id="element-4536" data-mp="1izmmuqg6qjcx" />
                          Tempo stimato: ~30s
                        </span>
                        <span data-id="element-4537" data-mp="u86whw5kfuwm">Report: {selectedType}</span>
                        <span data-id="element-4538" data-mp="p92p9b27c78x">Formato: {selectedFormat}</span>
                      </div>
                    </> : <>
                      <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-6" data-id="element-4539" data-mp="k9hpiyf7juv5">
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" data-id="element-4540" data-mp="famyj9736zzy" />
                      </div>
                      <h3 className="text-[16px] font-semibold text-[#1A202C] mb-2" data-id="element-4541" data-mp="bbe19ucu8jzn">
                        Report generato con successo!
                      </h3>
                      <p className="text-[13px] text-gray-500 mb-6" data-id="element-4542" data-mp="1j8csulsokp7">
                        Il report {selectedType} in formato {selectedFormat} è
                        pronto per il download.
                      </p>
                      <div className="flex items-center gap-3" data-id="element-4543" data-mp="3npy91zoyeivw">
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors" data-id="element-4544" data-mp="3jp4jl1vv4w0w">
                          <Download className="w-4 h-4" data-id="element-4545" data-mp="1z7c7l0ykj4lw" />
                          <span className="text-[13px] font-medium" data-id="element-4546" data-mp="238fkbelbkkiw">
                            Scarica Report
                          </span>
                        </button>
                        <button onClick={handleCloseWizard} className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-4547" data-mp="2d74u0k32ayo2">
                          <span className="text-[13px] font-medium" data-id="element-4548" data-mp="1fhs0buy4q4fz">
                            Chiudi
                          </span>
                        </button>
                      </div>
                    </>}
                </div>}
            </div>

            {/* Modal Footer */}
            {wizardStep < 4 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-4549" data-mp="wt4uha84pb1c">
                <button onClick={() => {
            if (wizardStep === 1) {
              setIsWizardOpen(false);
            } else {
              setWizardStep(s => s - 1);
            }
          }} className="flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-medium text-gray-600 hover:text-[#1A202C] hover:bg-gray-100 rounded-lg transition-all" data-id="element-4550" data-mp="11s81h07o5rby">
                  <ChevronLeft className="w-4 h-4" data-id="element-4551" data-mp="13gtryhpvyc62" />
                  {wizardStep === 1 ? 'Annulla' : 'Indietro'}
                </button>

                {wizardStep < 3 ? <button onClick={() => setWizardStep(s => s + 1)} className="flex items-center gap-1.5 px-5 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors text-[13px] font-medium" data-id="element-4552" data-mp="as4m65dvxinz">
                    Avanti
                    <ChevronRight className="w-4 h-4" data-id="element-4553" data-mp="ks58m071wewe" />
                  </button> : <button onClick={startGeneration} className="flex items-center gap-2 px-5 py-2.5 bg-[#00B4D8] text-white rounded-lg hover:bg-[#009BB8] transition-colors text-[13px] font-medium" data-id="element-4554" data-mp="frhgtcazzu14">
                    <FileText className="w-4 h-4" data-id="element-4555" data-mp="1r7pcquvxc73t" />
                    Genera Report
                  </button>}
              </div>}
          </div>
        </div>}
    </div>;
}